import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useReactiveVar } from '@apollo/client';

import {
  ZoomLessonParticipantListQuery,
  ZoomLessonRosterListQuery,
  ZoomParticipantsCoverageListQuery,
  useAttachZoomInstanceToLessonMutation,
  useDetachZoomInstanceMutation,
  useQueueZoomParticipantsSyncMutation,
  useResolveZoomParticipantMutation,
  useZoomLessonParticipantListQuery,
  useZoomLessonRosterListQuery,
  useZoomParticipantsCoverageListQuery,
} from 'gqlhooks';
import { userStateVar } from 'core/state/userState';
import { zoomParticipantsErrorKey } from 'core/utils/zoomParticipants';
import { UserRole } from 'types';

import { DOFIN_MANAGER_ROLES } from '../Dofinansowanie/useDofinPermissions';
import { DOFIN_ROLES, ZOOM_ROLES } from '../Palette/paletteRoles';

/**
 * ТЗ 0092 — участники Zoom по занятиям группы: покрытие, заходы, разбор,
 * привязка проведения к занятию.
 *
 * Права — зеркало бэкенда (`zoom_recordings/schema/participants.py`):
 * чтение — `ZOOM_ACCESS_ROLES ∪ DOFIN_READ_ROLES`, мутации —
 * `ZOOM_ACCESS_ROLES ∪ DOFIN_MANAGER_ROLES`. Скрытие контрола — UX, а не
 * защита: каждое действие сервер проверяет сам.
 */

const union = (...lists: UserRole[][]): UserRole[] =>
  lists.reduce<UserRole[]>(
    (acc, list) => [...acc, ...list.filter((role) => !acc.includes(role))],
    [],
  );

export const ZOOM_PARTICIPANTS_READ_ROLES = union(ZOOM_ROLES, DOFIN_ROLES);
export const ZOOM_PARTICIPANTS_WRITE_ROLES = union(
  ZOOM_ROLES,
  DOFIN_MANAGER_ROLES,
);

export interface IZoomParticipantsPermissions {
  canRead: boolean;
  canWrite: boolean;
  /** Выгрузка логов группы — только `DOFIN_READ_ROLES` (методисту нельзя). */
  canExportReport: boolean;
}

export const useZoomParticipantsPermissions = (): IZoomParticipantsPermissions => {
  const userState = useReactiveVar(userStateVar);
  const roles = (userState.me?.roles ?? []) as UserRole[];
  const has = (list: UserRole[]) => list.some((role) => roles.includes(role));
  return {
    canRead: has(ZOOM_PARTICIPANTS_READ_ROLES),
    canWrite: has(ZOOM_PARTICIPANTS_WRITE_ROLES),
    canExportReport: has(DOFIN_ROLES),
  };
};

export type IZoomLessonCoverage = ZoomParticipantsCoverageListQuery['zoomParticipantsCoverage'][number];
export type IZoomMeetingInstance = IZoomLessonCoverage['instances'][number];
export type IZoomParticipantSession = ZoomLessonParticipantListQuery['zoomLessonParticipants'][number];
export type IZoomRosterPerson = ZoomLessonRosterListQuery['zoomLessonRoster'][number];

interface IUseZoomParticipantsCoverageOptions {
  groupId: string;
  dateFrom?: string | null;
  dateTo?: string | null;
  skip?: boolean;
}

/** Покрытие занятий группы данными Zoom за период. */
export const useZoomParticipantsCoverage = ({
  groupId,
  dateFrom,
  dateTo,
  skip = false,
}: IUseZoomParticipantsCoverageOptions) => {
  const {
    data,
    previousData,
    loading,
    error,
    refetch,
  } = useZoomParticipantsCoverageListQuery({
    variables: {
      groupId,
      dateFrom: dateFrom || null,
      dateTo: dateTo || null,
    },
    skip: skip || !groupId,
    fetchPolicy: 'cache-and-network',
  });

  // Смена периода: прежние строки остаются (приглушённые) до ответа —
  // иначе таблица на секунду исчезала бы вместе с раскрытым занятием.
  const source = data ?? (loading ? previousData : undefined);
  const lessons: IZoomLessonCoverage[] = useMemo(
    () => source?.zoomParticipantsCoverage ?? [],
    [source],
  );

  return { lessons, loading, error, refetch };
};

/**
 * Заходы Zoom по занятию. ⚠️ Имена и e-mail гостей — ПДн: показываются только
 * в таблице разбора, запрос уходит только при раскрытом занятии.
 */
export const useZoomLessonParticipants = (
  lessonId: string | null,
  { skip = false }: { skip?: boolean } = {},
) => {
  const { data, loading, error, refetch } = useZoomLessonParticipantListQuery({
    variables: { lessonId: lessonId || '' },
    skip: skip || !lessonId,
    fetchPolicy: 'cache-and-network',
  });

  const sessions: IZoomParticipantSession[] = useMemo(
    () => data?.zoomLessonParticipants ?? [],
    [data],
  );

  return { sessions, loading, error, refetch };
};

/** Список занятия для «Это …»: студенты с посещаемостью и договорами. */
export const useZoomLessonRoster = (
  lessonId: string | null,
  { skip = false }: { skip?: boolean } = {},
) => {
  const { data, loading, error } = useZoomLessonRosterListQuery({
    variables: { lessonId: lessonId || '' },
    skip: skip || !lessonId,
    fetchPolicy: 'cache-first',
  });

  const roster: IZoomRosterPerson[] = useMemo(
    () => data?.zoomLessonRoster ?? [],
    [data],
  );

  return { roster, loading, error };
};

export interface IZoomParticipantsActionResult {
  success: boolean;
  /** Синк/забор поставлен в очередь. */
  queued?: boolean;
  /** Ключ перевода отказа (по коду сервера). */
  errorKey?: string;
  /** Код сервера как есть — для тестов и отладки, на экран не выводится. */
  errorCode?: string;
}

interface ICustomError {
  code?: string | null;
}

interface IPayload {
  success?: boolean | null;
  customErrors?: (ICustomError | null)[] | null;
}

/**
 * Разбор ответа мутации: успех — только `success: true`; отказ — ключ по коду
 * первой ошибки; брошенное исключение (сеть, отказ прав) — «неизвестная
 * ошибка», сырой текст сервера на экран не попадает.
 */
const toResult = (
  payload: IPayload | null | undefined,
  extra: Partial<IZoomParticipantsActionResult> = {},
): IZoomParticipantsActionResult => {
  if (payload?.success) return { success: true, ...extra };
  const code =
    (payload?.customErrors ?? []).find((item) => item?.code)?.code ?? undefined;
  return {
    success: false,
    errorCode: code,
    errorKey: zoomParticipantsErrorKey(code),
  };
};

const failure = (): IZoomParticipantsActionResult => ({
  success: false,
  errorKey: zoomParticipantsErrorKey(null),
});

export type ZoomParticipantsAction =
  | 'sync'
  | 'match'
  | 'ignore'
  | 'reset'
  | 'link'
  | 'unlink';

export interface IMatchParticipantInput {
  sessionId: string;
  /** Человек из списка занятия. Без него и без `ignore` — сброс. */
  userId?: string | null;
  ignore?: boolean;
  /** Запомнить, как этот человек называет себя в Zoom (алиас группы). */
  remember?: boolean;
}

/**
 * Мутации карточки «Участники Zoom». После каждой вызывающий перечитывает
 * СВОЙ наблюдаемый запрос через `refetch` его хука — без `refetchQueries` по
 * имени (тот бьёт и по пропущенным запросам). Заходы, которые вернул разбор,
 * Apollo обновляет в кеше сам — по `id`.
 */
export const useZoomParticipantsActions = () => {
  const [queueSync] = useQueueZoomParticipantsSyncMutation();
  const [resolveParticipant] = useResolveZoomParticipantMutation();
  const [attachInstance] = useAttachZoomInstanceToLessonMutation();
  const [detachInstance] = useDetachZoomInstanceMutation();
  const [pending, setPending] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  const guard = useCallback(
    async (
      key: string,
      action: () => Promise<IZoomParticipantsActionResult>,
    ): Promise<IZoomParticipantsActionResult> => {
      setPending(key);
      try {
        return await action();
      } catch {
        return failure();
      } finally {
        // Вкладку могли переключить, пока шёл запрос.
        if (mountedRef.current) setPending(null);
      }
    },
    [],
  );

  const requestSync = useCallback(
    (groupId: string, dateFrom: string, dateTo: string, force = false) =>
      guard('sync', async () => {
        const result = await queueSync({
          variables: { groupId, dateFrom, dateTo, force },
        });
        const payload = result.data?.requestZoomParticipantsSync;
        return toResult(payload, { queued: Boolean(payload?.queued) });
      }),
    [guard, queueSync],
  );

  const matchParticipant = useCallback(
    ({ sessionId, userId, ignore, remember }: IMatchParticipantInput) => {
      let kind: ZoomParticipantsAction = 'reset';
      if (ignore) kind = 'ignore';
      else if (userId) kind = 'match';
      return guard(`${kind}:${sessionId}`, async () => {
        const result = await resolveParticipant({
          variables: {
            sessionId,
            userId: userId || null,
            ignore: ignore ? true : null,
            // `remember` имеет смысл только при выборе человека.
            remember: userId ? Boolean(remember) : null,
          },
        });
        return toResult(result.data?.matchZoomParticipant);
      });
    },
    [guard, resolveParticipant],
  );

  const linkInstance = useCallback(
    (
      lessonId: string,
      target: { instanceUuid?: string | null; meetingId?: string | null },
    ) =>
      guard(`link:${lessonId}`, async () => {
        const result = await attachInstance({
          variables: {
            lessonId,
            instanceUuid: target.instanceUuid || null,
            meetingId: target.meetingId || null,
          },
        });
        const payload = result.data?.linkZoomInstanceToLesson;
        return toResult(payload, { queued: Boolean(payload?.queued) });
      }),
    [guard, attachInstance],
  );

  const unlinkInstance = useCallback(
    (instanceId: string) =>
      guard(`unlink:${instanceId}`, async () => {
        const result = await detachInstance({ variables: { instanceId } });
        return toResult(result.data?.unlinkZoomInstance);
      }),
    [guard, detachInstance],
  );

  return {
    requestSync,
    matchParticipant,
    linkInstance,
    unlinkInstance,
    /** Ключ идущего действия: `sync`, `match:<id>`, `link:<lessonId>`, … */
    pending,
  };
};
